import React, { FunctionComponent, useState } from "react"
import DatePicker from "react-datepicker"
import { useIntl } from "react-intl"
import { Card, CardItem } from "@walmart-web/livingdesign-components"
import { Calendar, ChevronDown } from "@walmart-web/livingdesign-icons"
import { addDaysToDate, formatDateWithMonth, getToday } from "helpers/temporal"
import { LANGUAGE_ES } from "constants/language"
import "./index.scss"

const twoMonths = 2

const getCalendarStartDay = (locale: string) => {
	const calendarStartDayOnMonday = 1
	const calendarStartDayOnSunday = 0
	return locale === LANGUAGE_ES
		? calendarStartDayOnMonday
		: calendarStartDayOnSunday
}

interface CalendarProps {
	initialValues: { from: Date; to: Date }
	maxDate?: Date
	minDate?: Date
	maxInterval?: number
}

const LivingDesignDatePicker: FunctionComponent<CalendarProps> = ({
	initialValues,
	maxDate = getToday(),
	minDate = null,
	maxInterval = null,
}) => {
	const intl = useIntl()
	const [startDate, setStartDate] = useState<Date>(initialValues.from)
	const [endDate, setEndDate] = useState<Date>(initialValues.to)
	const [showCalendar, setShowCalendar] = useState<boolean>(false)
	const [maxDateFromInterval, setMaxDateFromInterval] = useState<Date>(maxDate)

	const handleOnChange = ([start, end]: Array<Date>) => {
		setStartDate(start)
		setEndDate(end)
		updateMaxDate(start)

		if (start !== null && end !== null) {
			setShowCalendar(false)
		}
	}

	const updateMaxDate = (startDate: Date) => {
		if (startDate !== null && maxInterval !== null) {
			setMaxDateFromInterval(addDaysToDate(startDate, maxInterval))
		}
	}

	return (
		<div className="living-design-datepicker" role="living-design-datepicker">
			<div className="date-card-container">
				<Card
					role="card-item"
					UNSAFE_className="card-item"
					size="small"
					onClick={() => setShowCalendar(showCalendar ? false : true)}
				>
					<CardItem UNSAFE_className="datepicker-label-container">
						<div className="datepicker-label">
							<Calendar size="small" />
							<span>{`${formatDateWithMonth(
								startDate,
								intl.locale
							)} - ${formatDateWithMonth(endDate, intl.locale)}`}</span>
						</div>
						<ChevronDown size="small" />
					</CardItem>
				</Card>
			</div>
			{showCalendar && (
				<div className="datepicker-container" role="datepicker">
					<DatePicker
						minDate={minDate}
						selected={startDate}
						startDate={startDate}
						endDate={endDate}
						onChange={(dates: [Date, Date]) => handleOnChange(dates)}
						monthsShown={twoMonths}
						maxDate={maxDateFromInterval}
						locale={intl.locale}
						calendarStartDay={getCalendarStartDay(intl.locale)}
						selectsRange
						inline
					/>
				</div>
			)}
		</div>
	)
}

export default LivingDesignDatePicker
